import { useState, useCallback, useRef, useEffect } from 'react';
import { useMapStore } from '../../store/mapStore';
import SignatureTable from '../signatures/SignatureTable';
import SignatureImport from '../signatures/SignatureImport';

const STORAGE_KEY = 'wh-mapper-signatures-panel';

interface PanelState {
  x: number;
  y: number;
  width: number;
  height: number;
  isCollapsed: boolean;
}

const DEFAULT_STATE: PanelState = {
  x: 20,
  y: 20,
  width: 450,
  height: 400,
  isCollapsed: false,
};

// Load saved state from localStorage
function loadPanelState(): PanelState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_STATE, ...parsed };
    }
  } catch {
    // Ignore parse errors
  }
  return DEFAULT_STATE;
}

// Save state to localStorage
function savePanelState(state: PanelState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Ignore storage errors
  }
}

interface FloatingSignaturesPanelProps {
  systemId?: string | null;
  systemName?: string | null;
  wormholeClass?: string | null;
}

export default function FloatingSignaturesPanel({
  systemId,
  systemName,
  wormholeClass,
}: FloatingSignaturesPanelProps) {
  const { signatures } = useMapStore();

  // Load initial state from localStorage
  const [position, setPosition] = useState(() => {
    const saved = loadPanelState();
    return { x: saved.x, y: saved.y };
  });
  const [size, setSize] = useState(() => {
    const saved = loadPanelState();
    return { width: saved.width, height: saved.height };
  });
  const [isCollapsed, setIsCollapsed] = useState(() => loadPanelState().isCollapsed);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [showImport, setShowImport] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);

  // Check if a system is selected
  const hasSystem = !!systemId && !!systemName;

  // Get signature count for this system
  const signatureCount = hasSystem ? signatures.filter((s) => s.systemId === systemId).length : 0;

  // Save state when position, size, or collapsed state changes
  useEffect(() => {
    savePanelState({
      x: position.x,
      y: position.y,
      width: size.width,
      height: size.height,
      isCollapsed,
    });
  }, [position, size, isCollapsed]);

  // Handle mouse down on header (start drag)
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only drag from the header area, not from buttons
      const target = e.target as HTMLElement;
      if (target.closest('.panel-header') && !target.closest('button')) {
        setIsDragging(true);
        const rect = panelRef.current?.getBoundingClientRect();
        if (rect) {
          setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
          });
        }
        e.preventDefault();
      }
    },
    []
  );

  // Handle resize start
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height,
      });
    },
    [size]
  );

  // Handle mouse move (dragging or resizing)
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = Math.max(0, e.clientX - dragOffset.x);
        const newY = Math.max(0, e.clientY - dragOffset.y);
        setPosition({ x: newX, y: newY });
      } else if (isResizing) {
        const deltaX = e.clientX - resizeStart.x;
        const deltaY = e.clientY - resizeStart.y;
        setSize({
          width: Math.max(350, resizeStart.width + deltaX),
          height: Math.max(200, resizeStart.height + deltaY),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragOffset, resizeStart]);

  // Toggle collapsed state
  const toggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  // Toggle import section
  const toggleImport = useCallback(() => {
    setShowImport((prev) => !prev);
  }, []);

  return (
    <div
      ref={panelRef}
      className={`
        fixed z-50 rounded-lg border border-gray-600 bg-gray-900/95 shadow-xl backdrop-blur-sm
        ${isDragging ? 'cursor-grabbing select-none' : ''}
        ${isResizing ? 'select-none' : ''}
      `}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: isCollapsed ? 'auto' : size.height,
        minWidth: 350,
        minHeight: isCollapsed ? 'auto' : 200,
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Panel header - draggable */}
      <div
        className={`
          panel-header flex items-center justify-between px-3 py-2 border-b border-gray-700
          ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}
        `}
      >
        <div className="flex items-center gap-2">
          {/* Signature count badge */}
          <span className="flex items-center justify-center w-6 h-6 rounded bg-blue-600 text-xs font-bold text-white">
            {signatureCount}
          </span>

          {/* System info */}
          {hasSystem ? (
            <span className="text-sm text-gray-300">
              Signatures in{' '}
              {wormholeClass && (
                <span className="font-bold text-blue-400">{wormholeClass}</span>
              )}{' '}
              <span className="font-semibold text-white">{systemName}</span>
            </span>
          ) : (
            <span className="text-sm text-gray-500">Signatures</span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Import toggle button - only show when system selected */}
          {hasSystem && (
            <button
              onClick={toggleImport}
              className={`px-2 py-1 rounded text-xs transition ${
                showImport ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
              title="Toggle import section"
            >
              Import
            </button>
          )}

          {/* Collapse toggle */}
          <button
            onClick={toggleCollapse}
            className="p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-white transition"
            title={isCollapsed ? 'Expand' : 'Collapse'}
          >
            {isCollapsed ? '▼' : '▲'}
          </button>
        </div>
      </div>

      {/* Panel content */}
      {!isCollapsed && (
        <div
          className="p-3 overflow-y-auto"
          style={{ height: `calc(100% - 44px)` }}
        >
          {hasSystem ? (
            <>
              {/* Import section (collapsible) */}
              {showImport && (
                <div className="mb-3">
                  <SignatureImport
                    systemId={systemId!}
                    onImportComplete={() => {
                      // Could auto-hide import after successful import
                    }}
                  />
                </div>
              )}

              {/* Signature table */}
              <SignatureTable systemId={systemId!} />
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500 text-sm">
              Click a system to view signatures
            </div>
          )}
        </div>
      )}

      {/* Resize handle (bottom-right corner) */}
      {!isCollapsed && (
        <div
          className="absolute bottom-0 right-0 w-4 h-4 cursor-se-resize"
          onMouseDown={handleResizeStart}
        >
          <svg
            className="w-4 h-4 text-gray-500 hover:text-gray-300 transition"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M22 22H20V20H22V22ZM22 18H20V16H22V18ZM18 22H16V20H18V22ZM22 14H20V12H22V14ZM18 18H16V16H18V18ZM14 22H12V20H14V22Z" />
          </svg>
        </div>
      )}
    </div>
  );
}
